import { useState, useEffect } from "react";
import axios from "axios";
import IntakeFormSteps from "../components/IntakeForm";
import { CheckCircle2, CreditCard, BookOpen, Plus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntakePage() {
  const [data, setData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [intakeId, setIntakeId] = useState(null);
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/addons`).then(res => setAddons(res.data.filter(a => a.active))).catch(() => {});
  }, []);

  const handleComplete = async (signature) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        signature,
        submitted_at: new Date().toISOString(),
        selected_addons: selectedAddons,
      };
      const res = await axios.post(`${API}/intake`, payload);
      setIntakeId(res.data.id);
      setSubmitted(true);
      toast.success("Form submitted! Now select your package and pay.");
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleAddon = (id) => {
    setSelectedAddons(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };

  const addonTotal = selectedAddons.reduce((sum, id) => {
    const addon = addons.find(a => a.id === id);
    return sum + (addon ? addon.price : 0);
  }, 0);

  const hasCurriculum = selectedAddons.includes("curriculum");
  const baseSetup = 999;
  const firstMonth = hasCurriculum ? 0 : 199;
  const totalToday = baseSetup + firstMonth + addonTotal;

  const handlePay = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/stripe`, {
        package: selectedAddons.length > 0 ? "custom" : "setup_and_subscription",
        origin_url: origin,
        payment_method: "stripe",
        intake_id: intakeId,
        addon_ids: selectedAddons,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="intake-page">
      <section className="bg-[#1C2E4A] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Get Started</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            {submitted ? "Choose Your Package" : "Start Your Church School"}
          </h1>
          <p className="mt-4 text-base text-white/70 max-w-2xl">
            {submitted
              ? "Your information has been submitted. Select your package and complete payment to get started."
              : "Fill out the form below to tell us about your school. After submitting, you'll proceed to payment."}
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!submitted ? (
            <IntakeFormSteps data={data} setData={setData} onComplete={handleComplete} />
          ) : (
            <div>
              {/* Submission confirmed */}
              <div className="bg-[#8F9779]/10 rounded-xl border border-[#8F9779]/30 p-6 mb-8 flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#8F9779] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-serif text-lg font-semibold text-[#1C2E4A]">Form Submitted Successfully</h3>
                  <p className="text-sm text-[#595959]">We have all your school details. Now choose your package and pay to get started.</p>
                </div>
              </div>

              {/* Package selection */}
              <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-6">
                <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-4">Your Package</h3>

                {/* Base package */}
                <div className="bg-white rounded-lg border border-[#E5E2DC] p-5 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-[#1C2E4A]">Church School System</p>
                      <p className="text-xs text-[#595959]">Custom website, enrollment, admin dashboard, parent portal, automated emails + ongoing support</p>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-xl font-bold text-[#1C2E4A]">$999</p>
                      <p className="text-xs text-[#595959]">setup + $199/mo</p>
                    </div>
                  </div>
                </div>

                {/* Add-ons */}
                <h4 className="font-medium text-[#1C2E4A] text-sm mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#C8A97E]" /> Available Add-Ons
                </h4>
                <div className="space-y-2 mb-4">
                  {addons.map(addon => (
                    <div
                      key={addon.id}
                      data-testid={`addon-${addon.id}`}
                      className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${
                        selectedAddons.includes(addon.id) ? "border-[#C8A97E] bg-[#C8A97E]/5" : "border-[#E5E2DC] hover:border-[#C8A97E]/50"
                      }`}
                      onClick={() => toggleAddon(addon.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedAddons.includes(addon.id) ? "bg-[#C8A97E] border-[#C8A97E]" : "border-[#E5E2DC]"
                          }`}>
                            {selectedAddons.includes(addon.id) && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-[#1C2E4A]">{addon.name}</p>
                            <p className="text-xs text-[#595959]">{addon.description}</p>
                          </div>
                        </div>
                        <p className="font-serif font-bold text-[#1C2E4A] shrink-0 ml-4">+${addon.price.toLocaleString()}</p>
                      </div>
                      {addon.id === "curriculum" && selectedAddons.includes("curriculum") && (
                        <div className="mt-2 ml-8 p-2 bg-[#8F9779]/10 rounded-lg">
                          <p className="text-xs text-[#8F9779] font-medium">First month free when you add curriculum! ($199 savings)</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="bg-white rounded-lg border border-[#E5E2DC] p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#595959]">Setup fee</span>
                    <span className="text-[#1A1A1A]">$999</span>
                  </div>
                  {!hasCurriculum && (
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-[#595959]">First month</span>
                      <span className="text-[#1A1A1A]">$199</span>
                    </div>
                  )}
                  {hasCurriculum && (
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-[#8F9779] font-medium">First month free (curriculum bonus)</span>
                      <span className="text-[#8F9779] line-through">$199</span>
                    </div>
                  )}
                  {selectedAddons.length > 0 && (
                    <>
                      <div className="border-t border-[#E5E2DC] my-2" />
                      {selectedAddons.map(id => {
                        const addon = addons.find(a => a.id === id);
                        return addon ? (
                          <div key={id} className="flex items-center justify-between text-sm mt-1">
                            <span className="text-[#595959]">{addon.name}</span>
                            <span className="text-[#1A1A1A]">${addon.price.toLocaleString()}</span>
                          </div>
                        ) : null;
                      })}
                    </>
                  )}
                  <div className="border-t border-[#E5E2DC] mt-3 pt-3 flex items-center justify-between">
                    <span className="font-medium text-[#1C2E4A]">Due today</span>
                    <span data-testid="intake-total" className="font-serif text-2xl font-bold text-[#1C2E4A]">${totalToday.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-[#595959]/60 mt-1">
                    {hasCurriculum ? "Then $199/month starting next month" : "Then $199/month ongoing"}
                  </p>
                  <p className="text-xs text-[#595959]/50 mt-1">Domain included free first year; $25 renewal at month 12.</p>
                </div>
              </div>

              {/* Pay button */}
              <button
                data-testid="intake-pay-btn"
                onClick={handlePay}
                disabled={loading}
                className="w-full bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-3.5 rounded-md transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-base"
              >
                <CreditCard className="w-5 h-5" />
                {loading ? "Processing..." : `Pay $${totalToday.toLocaleString()} & Get Started`}
              </button>
              <p className="text-xs text-[#595959]/60 text-center mt-3">Secure payment processed through Stripe.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
